import React, { useState } from "react";
// react redux
import { useSelector } from "react-redux";
// components
import StatisticsCard from "../../components/cards/StatisticsCard";
import PageWrapper from "../../components/page/PageWrapper";
import PageTitle from "../../components/page/PageTitle";
import FiltersContainer from "../../components/filters/FiltersContainer";
import FromDateFilter from "../../components/filters/FromDateFilter";
import ToDateFilter from "../../components/filters/ToDateFilter";
import ErrorSection from "../../components/alerts/ErrorSection";
import HeardAboutDonutChart from "./components/HeardAboutDonutChart";
// mui
import { Skeleton, Stack } from "@mui/material";
// utils
import { RANDOM_COLORS } from "../../utils/RandomColors";
// fetching
import { useQuery } from "@tanstack/react-query";
import fetching from "../../helpers/fetching/fetching";
// hooks
import useFilterParams from "../../hooks/useFilterParams";
// types
import type { UserState } from "../../types/types";
// utils
import fireAlert from "../../helpers/alerts/fireAlert";
// icons
import { LuUser2, LuStore } from "react-icons/lu";
import { BiCategory } from "react-icons/bi";
import { MdOutlineDiscount, MdOutlineMessage } from "react-icons/md";
import { BsBuildings } from "react-icons/bs";
import { FaRegBell, FaQuestion } from "react-icons/fa6";
import { GoSearch } from "react-icons/go";
import { LiaBusinessTimeSolid } from "react-icons/lia";

type Stats = {
  query: {
    from: string;
    to: string;
  };
  total_categories: number;
  total_users: number;
  total_businesses: number;
  total_pending_businesses: number;
  total_faqs: number;
  total_tags: number;
  total_cities: number;
  total_empty_search: number;
  total_notifications: number;
  total_suggestions: number;
};

type OtpStats = {
  wallet: {
    trx_count_credit: number;
    credit: number;
  };
};
const Home = () => {
  const userToken = useSelector((state: UserState) => state.user.userToken);
  const { from, to } = useFilterParams();

  // {"query":{"from":"0001-01-01T00:00:00Z","to":"2024-12-21T17:16:14.521601523Z"},"total_users":41,"total_businesses":54,"total_categories":44,"total_faqs":3,"total_tags":51,"total_cities":24,"total_empty_search":241,"total_notifications":75,"total_suggestions":14}

  const getStatistics = () => {
    let endpoint = "/statistics?";
    if (to) endpoint += "to=" + to;
    if (to && from) endpoint += "&";
    if (from) endpoint += "from=" + from;
    return fetching(endpoint, userToken)
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          throw data.message || "Error";
        }
        return data;
      })
      .catch((err) => fireAlert(err.toString(), "error"));
  };

  const getOtpStatistics = () => {
    return fetching("/me/otp", userToken)
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          throw data.message || "Error";
        }
        return data;
      })
      .catch((err) => fireAlert(err.toString(), "error"));
  };

  const {
    data: stats,
    isLoading,
    isError,
    error,
  } = useQuery<Stats, any>({
    queryKey: ["stats", from, to],
    queryFn: getStatistics,
  });

  const {
    data: otpStats,
    isLoading: otpStatsLoading,
    isError: otpStatsIsError,
    error: otpStatsError,
  } = useQuery<OtpStats, any>({
    queryKey: ["otp-stats", from, to],
    queryFn: getOtpStatistics,
  });
  return (
    <PageWrapper>
      <PageTitle title="الرئيسية" />
      <FiltersContainer>
        <FromDateFilter />
        <ToDateFilter />
      </FiltersContainer>
      {isLoading ? (
        <Stack
          direction="row"
          gap="16px"
          justifyContent="center"
          flexWrap="wrap"
        >
          {Array(3)
            .fill(0)
            .map((item) => (
              <Skeleton
                height="72px"
                width="266px"
                sx={{ transform: "scale(1,1)" }}
              />
            ))}
        </Stack>
      ) : isError ? (
        <ErrorSection errorMsg={error.toString()} />
      ) : (
        <Stack
          direction="row"
          gap="16px"
          justifyContent="center"
          flexWrap="wrap"
        >
          <StatisticsCard
            color={RANDOM_COLORS[0]}
            title="عدد المستخدمين"
            Icon={LuUser2}
            value={stats?.total_users ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[1]}
            title="عدد الانشطة التجارية و الخدمة"
            Icon={LuStore}
            value={stats?.total_businesses ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[2]}
            title="عدد الانشطة التجارية و الخدمة المعلقة"
            Icon={LiaBusinessTimeSolid}
            value={stats?.total_pending_businesses ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[3]}
            title="عدد التصنيفات"
            Icon={BiCategory}
            value={stats?.total_categories ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[4]}
            title="عدد الاسئلة الاكثر تكرارا"
            Icon={FaQuestion}
            value={stats?.total_faqs ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[5]}
            title="عدد الوسوم"
            Icon={MdOutlineDiscount}
            value={stats?.total_tags ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[6]}
            title="عدد المدن"
            Icon={BsBuildings}
            value={stats?.total_cities ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[7]}
            title="عدد نتائج البحث الفارغة"
            Icon={GoSearch}
            value={stats?.total_empty_search ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[8]}
            title="عدد الاشعارات"
            Icon={FaRegBell}
            value={stats?.total_notifications ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[9]}
            title="عدد الاستفسارات و الاقتراحات"
            Icon={MdOutlineMessage}
            value={stats?.total_suggestions ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[0]}
            title="الرصيد المتبقي للرسائل النصية"
            Icon={LuStore}
            value={
              otpStats?.wallet?.trx_count_credit
                ? `${otpStats?.wallet?.credit}  د.ل`
                : 0
            }
          />
        </Stack>
      )}
      <HeardAboutDonutChart from={from} to={to} />
    </PageWrapper>
  );
};
export default Home;
